import { AbstractControl, ValidatorFn } from '@angular/forms';

export interface ComplexityRequirement {
  text: string;
  satisfied: boolean;
}

export type PasswordStrength = 'weak' | 'good' | 'strong';

export const MIN_PASSWORD_LENGTH = 8;
const DIGIT_REGEX = /\d/;
const LOWER_REGEX = /[a-z]/;
const UPPER_REGEX = /[A-Z]/;
const SYM_REGEX = /[ |!"#$%&'()*+,\-./:;<=>?@[\]^_`{}~]+/;
export const passwordsMatchValidator =
  (passwordControlName: string): ValidatorFn =>
  (ctrl) => {
    const passwordControl = ctrl.parent?.get(passwordControlName);

    const matches = passwordControl?.value && passwordControl.value === ctrl?.value;
    return !matches ? { noMatch: { value: passwordControl?.value } } : null;
  };

export const getPasswordComplexity = (): ComplexityRequirement[] => [
  {
    satisfied: false,
    text: `Must be at least ${MIN_PASSWORD_LENGTH} characters long`
  },
  {
    satisfied: false,
    text: 'Must contain uppercase and lowercase letters'
  },
  {
    satisfied: false,
    text: 'Must contain a number'
  },
  {
    satisfied: false,
    text: 'Must contain a symbol'
  },
  {
    satisfied: true,
    text: 'Cannot contain: name, username, or email'
  }
];

const createCustomRegexValidator =
  (reg: RegExp, getError: (value: object) => object): ValidatorFn =>
  (control) => {
    const value = control.value;
    const isValid = typeof value === 'string' && reg.test(value);
    if (isValid) return null;

    return getError(value);
  };

export const hasDigitValidator = createCustomRegexValidator(DIGIT_REGEX, () => ({ digit: 'Must contain a number' }));
export const hasLowerCaseValidator = createCustomRegexValidator(LOWER_REGEX, () => ({
  lower: 'Must contain lower case character'
}));

export const hasUpperCaseValidator = createCustomRegexValidator(UPPER_REGEX, () => ({
  upper: 'Must contain upper case character'
}));

export const hasSymbolValidator = createCustomRegexValidator(SYM_REGEX, () => ({
  symbol: 'Must contain a symbol'
}));

export const noBlacklistedStringsValidator =
  (getStrings: (control: AbstractControl) => string[], getError: (value: object) => object): ValidatorFn =>
  (ctrl) => {
    const strings = getStrings(ctrl);
    const value = ('' + ctrl.value).toLowerCase();
    if (strings.some((s) => value.includes(s.toLowerCase()))) {
      return getError(ctrl);
    }
    return null;
  };
